import { TIEMPO_EN_NOTA_INACTIVIDAD_MS, TIEMPO_EN_NOTA_TOPE_S } from '@ahora-patagonia/contracts';

/**
 * Cuánto tiempo estuvo una persona **leyendo** una nota.
 *
 * Es una clase y no un `useEffect` con variables sueltas porque es la única pieza del cliente con
 * lógica que se puede equivocar sin fallar: un reloj mal llevado no rompe nada, solo hace que el
 * tiempo medio del medio entero sea un número plausible y falso.
 *
 * Cuenta el tiempo que la pestaña está **visible y activa**, con tres reglas:
 *
 *  - **La pestaña oculta no cuenta.** Una nota abierta en una pestaña de fondo se lee después o no
 *    se lee.
 *  - **Sin interacción tampoco.** Pasados `TIEMPO_EN_NOTA_INACTIVIDAD_MS` sin scroll, puntero ni
 *    teclado, el reloj se pausa y el tiempo de esa pausa no se cuenta. Es lo que hace honesto al
 *    tope de 20 minutos: sin el corte, una pestaña visible en un segundo monitor sumaría veinte
 *    minutos de "lectura" sola.
 *  - **Hay un techo.** Aunque todo lo anterior se cumpla, nunca reporta más que el tope.
 *
 * El reloj lo pone quien la usa (`ahora`), así que el test controla el tiempo sin dormir.
 */
export class RelojDeLectura {
  private acumuladoMs = 0;
  /** Cuándo arrancó el tramo en curso. `null` si está pausado. */
  private desde: number | null = null;
  /** Último signo de vida: scroll, puntero o teclado. */
  private ultimaSenalDeVida: number;

  constructor(ahora: number) {
    this.ultimaSenalDeVida = ahora;
    this.desde = ahora;
  }

  /** Hubo interacción: si estaba pausado por inactividad, vuelve a correr. */
  hayVida(ahora: number): void {
    this.cerrarTramo(ahora);
    this.ultimaSenalDeVida = ahora;
    if (this.desde === null) this.desde = ahora;
  }

  /** La pestaña se ocultó (o la persona se fue de la nota): se corta el tramo. */
  pausar(ahora: number): void {
    this.cerrarTramo(ahora);
    this.desde = null;
  }

  /** La pestaña volvió a estar visible. */
  reanudar(ahora: number): void {
    if (this.desde === null) this.desde = ahora;
    this.ultimaSenalDeVida = ahora;
  }

  /** Los segundos a reportar, ya topeados. `0` si no llegó a acumularse un segundo entero. */
  segundos(ahora: number): number {
    this.cerrarTramo(ahora);
    if (this.desde !== null) this.desde = ahora;
    return Math.min(Math.floor(this.acumuladoMs / 1_000), TIEMPO_EN_NOTA_TOPE_S);
  }

  /**
   * Cierra el tramo abierto y suma lo que corresponda.
   *
   * **El tramo se corta en la última señal de vida y no en `ahora`**: si la persona dejó de
   * interactuar hace cinco minutos, esos cinco minutos no son lectura. Ese detalle es el que separa
   * "tiempo con la nota en pantalla" de "tiempo leyendo", y es el que se olvida siempre.
   */
  private cerrarTramo(ahora: number): void {
    if (this.desde === null) return;
    const corte = Math.min(ahora, this.ultimaSenalDeVida + TIEMPO_EN_NOTA_INACTIVIDAD_MS);
    if (corte > this.desde) this.acumuladoMs += corte - this.desde;
    // Si la inactividad ya venció, el reloj queda pausado hasta que haya vida de nuevo.
    this.desde = ahora - this.ultimaSenalDeVida > TIEMPO_EN_NOTA_INACTIVIDAD_MS ? null : ahora;
  }
}
